import { makeStyles } from '@material-ui/core/styles';
import { SecondaryColors, TextColors, TextOpacities } from '@/utils/constants';
import { hexToRGBA } from '@/utils/helpers';

const lightTabTheme = {
  root: {},
};

const darkTabTheme = {
  root: {},
};

const getLightTheme = (muiTheme: any) => ({
  root: {
    '& $content': {
      color: TextColors.dark,
    },
    '& $cta': {
      // color: TextColors.dark,
      color: 'blue',
    },

    '& $headerText': {
      color: TextColors.dark,

      [muiTheme.breakpoints.up('md')]: {
        color: TextColors.dark,
      },
    },
    '& $tabs': {
      '&:before': {
        borderBottom: `1px solid ${hexToRGBA(TextColors.dark, TextOpacities.light4)}`,
      },
      '& .MuiTabs-scroller .MuiTabs-indicator': { backgroundColor: SecondaryColors.dark },
    },
    '& $tab': {
      '&.MuiTab-textColorInherit': {
        color: hexToRGBA(TextColors.dark, TextOpacities.light2),

        '&.Mui-selected, &:hover': {
          color: TextColors.dark,
        },
      },
    },
    '& $superheading': {
      color: SecondaryColors.dark,
    },
    '& $rtc': {
      color: hexToRGBA(TextColors.dark, TextOpacities.light2),

      '& b, & strong': {
        color: TextColors.dark,
      },
    },
    '& $tabIndicator': {
      backgroundColor: SecondaryColors.dark,
    },
  },
});

const getDarkTheme = (muiTheme: any) => ({
  root: {
    '& $tabs': {
      '&:before': {
        borderBottom: `1px solid ${hexToRGBA(TextColors.light, TextOpacities.light4)}`,
      },
      '& .MuiTabs-scroller .MuiTabs-indicator': {
        backgroundColor: `${SecondaryColors.main} !important`,
      },
    },
    '& $tab': {
      '&.MuiTab-textColorInherit': {
        color: hexToRGBA(TextColors.light, TextOpacities.dark2),

        '&.Mui-selected, &:hover': {
          color: TextColors.light,
        },
      },
    },
    '& $headerText': {
      color: TextColors.light,
      [muiTheme.breakpoints.up('md')]: {
        color: TextColors.light,
      },
    },
    '& $superheading': {
      color: SecondaryColors.main,
    },
    '& $rtc': {
      color: hexToRGBA(TextColors.light, TextOpacities.light2),

      '& b, & strong': {
        color: TextColors.light,
      },
    },
    '& $tabIndicator': {
      backgroundColor: SecondaryColors.main,
    },
    '& $content': {
      color: TextColors.light,
    },
    '& $cta': {
      color: TextColors.light,
    },
  },
});

const useStyles = makeStyles(
  (muiTheme) => ({
    root: {
      '&.light-theme': getLightTheme(muiTheme).root,
      '&.dark-theme': getDarkTheme(muiTheme).root,
    },

    content: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      alignContent: 'flex-start',
      '&.light-theme': getLightTheme(muiTheme).root,
      '&.dark-theme': getDarkTheme(muiTheme).root,
      '& .tabPanels': {
        marginBottom: '28px',
      },
      [muiTheme.breakpoints.up('md')]: {
        '&.display-inset': {
          padding: '32px 2px 0px 0px',
        },
      },
    },

    cta: {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
    },

    tabs: {
      '&.MuiTabs-root': {
        position: 'relative',
        display: 'inline-flex',

        '&:before': {
          content: '""',
          position: 'absolute',
          top: 0,
          bottom: 0,
          width: '100%',
        },

        [muiTheme.breakpoints.down('sm')]: {
          margin: '-2px -2px 0px -32px',
          width: 'calc(100% + 64px)',

          '& .MuiTabs-scrollable': {
            padding: '2px 2px 0px 2px',
            margin: '-2px -2px 0 -2px',
          },

          '& .MuiTabs-scrollButtons': {
            width: '32px',
          },

          '& + *': {
            marginTop: '28px',
          },
        },

        [muiTheme.breakpoints.up('md')]: {
          margin: '0',
          minHeight: '64px',
          maxWidth: '100%',
          width: '100%',

          '& .MuiTabs-flexContainer': {
            position: 'relative',
            width: '100%',
            justifyContent: 'space-evenly',
          },

          '& .MuiTabs-scrollable': {
            display: 'inline-flex',
            justifyContent: 'center',
          },
        },
      },
    },
    tabIndicator: {},
    tab: {
      '&.MuiTab-root': {
        flexDirection: 'column',
        justifyContent: 'flex-end',
      },

      '& .MuiTypography-subtitle1': {
        lineHeight: '2em',
        marginBottom: muiTheme.spacing(0.5),

        [muiTheme.breakpoints.up('md')]: {
          marginBottom: muiTheme.spacing(1.5),
        },
      },

      '&.MuiTab-textColorInherit': {
        opacity: 1,
      },
    },

    tabPanelAnimated: {
      '& > *': {
        animation: 'enter 450ms ease-out',
      },
    },

    tabPanel: {
      position: 'relative',

      [muiTheme.breakpoints.down('sm')]: {
        '&.right-column': {
          textAlign: 'center',
        },
      },

      [muiTheme.breakpoints.up('md')]: {
        width: '100%',

        '&.left-column': {
          alignItems: 'center',
          alignContent: 'center',
        },
      },
    },

    superheading: {
      '&.MuiTypography-root': {
        marginBottom: muiTheme.spacing(2),
      },
    },

    header: {
      marginBottom: muiTheme.spacing(6),
      textAlign: 'center',

      [muiTheme.breakpoints.down('sm')]: {
        marginBottom: muiTheme.spacing(3),
      },
    },

    headerText: {},

    heading: {
      marginTop: 0,

      '& p, & h1, & h2, & h3, & h4, & h5, & h6': {
        ...muiTheme.typography.h4,
      },

      '& + *': {
        marginTop: muiTheme.spacing(2),
      },
    },

    rtc: {},

    link: {
      '&:not(:first-child)': {
        marginTop: muiTheme.spacing(4),

        [muiTheme.breakpoints.down('sm')]: {
          width: '100%',
        },
      },
    },

    twoColumns: {
      borderRadius: '0 0 4px 4px',
      '&.light-theme': lightTabTheme.root,
      '&.dark-theme': darkTabTheme.root,

      [muiTheme.breakpoints.up('md')]: {
        padding: 0,
        marginTop: '32px',
        '&.display-inset': {
          padding: '32px',
          marginTop: 0,
        },
      },

      '& .columns': {
        alignItems: 'center',

        '& > *:not(:last-child)': {
          [muiTheme.breakpoints.down('sm')]: {
            order: 2,
            marginBottom: 0,
          },
        },

        '& > *:last-child': {
          [muiTheme.breakpoints.down('sm')]: {
            order: 1,
            marginBottom: muiTheme.spacing(3),
          },
        },
      },
    },
  }),
  { name: 'GenCTLPersonaTabs' },
);

export default useStyles;
